// Home (Today) screen layout: an ordered list of widgets placed into slots. The first two widgets
// form the top row; the rest share the bottom row(s). Rules for which widgets are allowed and how
// many live in the backend; this module only lays them out and edits the order.

export type HomeWidgetId =
  | 'clock' | 'now' | 'air' | 'agenda' | 'week' | 'chores' | 'habits'
  | 'salat' | 'salat_next' | 'salat_list' | 'hijri' | 'forecast' | 'salat_split' | 'calendar' | 'weather_hero'
  | 'verse' | 'exchange' | 'kanji';

/** The largest template's capacity; each template has its own (see HOME_TEMPLATES). */
export const MAX_HOME_WIDGETS = 6;

export const HOME_WIDGETS: { id: HomeWidgetId; label: string; desc: string }[] = [
  { id: 'clock', label: 'Clock', desc: 'Large time and date' },
  { id: 'now', label: 'Weather now', desc: 'Temperature and today’s hourly curve' },
  { id: 'agenda', label: 'Today’s plans', desc: 'The family calendar for today' },
  { id: 'calendar', label: 'Calendar', desc: 'Month view with the coming days’ plans' },
  { id: 'air', label: 'The air around you', desc: 'Air quality, wind, UV and more' },
  { id: 'week', label: 'The week ahead', desc: 'Seven-day forecast' },
  { id: 'weather_hero', label: 'Weather (big)', desc: 'Big temperature with the next few days' },
  { id: 'forecast', label: 'Today + week', desc: 'Today’s temperature with the week ahead' },
  { id: 'chores', label: 'Chores today', desc: 'What’s left to do today' },
  { id: 'habits', label: 'Habits today', desc: 'Today’s habits with streaks' },
  { id: 'salat', label: 'Salat times', desc: 'Prayer times, next prayer and Hijri date' },
  { id: 'salat_split', label: 'Salat (split)', desc: 'Next prayer on the left, every prayer on the right' },
  { id: 'salat_next', label: 'Next prayer', desc: 'Just the next prayer, large, with a countdown' },
  { id: 'salat_list', label: 'Prayer times', desc: 'Compact list of today’s prayer times' },
  { id: 'hijri', label: 'Hijri date', desc: 'Today’s Islamic date, large' },
  { id: 'verse', label: 'Verse of the day', desc: 'A Quran verse with its translation' },
  { id: 'exchange', label: 'Exchange rate', desc: 'Your currency pair, e.g. JPY → BDT' },
  { id: 'kanji', label: 'Kanji slideshow', desc: 'The most common kanji, one at a time' },
];

export const DEFAULT_HOME_LAYOUT: HomeWidgetId[] = ['clock', 'now', 'agenda', 'air', 'week'];

/** Fallback for the "Our family" scene until the server's saved list arrives. */
export const DEFAULT_FAMILY_LAYOUT: HomeWidgetId[] = ['salat_split', 'calendar', 'agenda', 'chores', 'habits', 'weather_hero'];

export function swapWidgets<T>(layout: T[], a: number, b: number): T[] {
  if (a === b || a < 0 || b < 0 || a >= layout.length || b >= layout.length) return layout;
  const next = layout.slice();
  next[a] = layout[b]!;
  next[b] = layout[a]!;
  return next;
}

/** Removes a widget, but never the last one (a home screen is never empty). */
export function removeWidget<T>(layout: T[], id: T): T[] {
  if (layout.length <= 1) return layout;
  return layout.filter((w) => w !== id);
}

export function addWidget<T>(layout: T[], id: T, capacity: number = MAX_HOME_WIDGETS): T[] {
  if (layout.length >= capacity || layout.indexOf(id) >= 0) return layout;
  return [...layout, id];
}

// ---- Templates ----------------------------------------------------------------------------

export type HomeTemplateId = 'classic' | 'dashboard' | 'quad' | 'columns' | 'spotlight';

export interface HomeTemplateDef {
  id: HomeTemplateId;
  label: string;
  desc: string;
  capacity: number;
  /** Widgets used to fill empty slots when this template is picked. */
  suggested: HomeWidgetId[];
  /** Dashboard: a fixed bar with chore pills and the time sits above the slots. */
  topBar: boolean;
}

export const HOME_TEMPLATES: HomeTemplateDef[] = [
  { id: 'classic', label: 'Classic', desc: 'Two on top, the rest below', capacity: 5, suggested: DEFAULT_HOME_LAYOUT, topBar: false },
  {
    id: 'dashboard',
    label: 'Dashboard',
    desc: 'Chores and time bar, 2 large, 4 small',
    capacity: 6,
    suggested: ['salat_split', 'weather_hero', 'agenda', 'chores', 'habits', 'calendar'],
    topBar: true,
  },
  { id: 'quad', label: 'Grid', desc: 'Four equal tiles', capacity: 4, suggested: ['clock', 'forecast', 'agenda', 'salat'], topBar: false },
  { id: 'columns', label: 'Columns', desc: 'Three tall columns', capacity: 3, suggested: ['agenda', 'salat', 'forecast'], topBar: false },
  { id: 'spotlight', label: 'Spotlight', desc: 'One large, two stacked', capacity: 3, suggested: ['calendar', 'clock', 'salat_next'], topBar: false },
];

export function templateDef(id: string | null | undefined): HomeTemplateDef {
  return HOME_TEMPLATES.find((t) => t.id === id) || HOME_TEMPLATES[0]!;
}

/**
 * Layout after picking a template: keeps the current widgets in order up to the template's
 * capacity, then fills free slots with the template's suggestions (skipping duplicates).
 */
export function applyTemplate(layout: HomeWidgetId[], template: HomeTemplateId): HomeWidgetId[] {
  const def = templateDef(template);
  const next = layout.slice(0, def.capacity);
  for (const id of def.suggested) {
    if (next.length >= def.capacity) break;
    if (next.indexOf(id) < 0) next.push(id);
  }
  return next;
}

/** Grid for a template. Fixed templates always lay out every slot; unfilled ones stay empty. */
export function templateGrid(template: HomeTemplateId, count: number, portrait: boolean): HomeGrid {
  const two = `${fr(1)} ${fr(1)}`;
  switch (template) {
    case 'dashboard':
      return portrait
        ? { columns: two, rows: `${fr(0.95)} ${fr(0.8)} ${fr(0.8)}`, areas: '"s0 s1" "s2 s3" "s4 s5"', narrow: [true, true, true, true, true, true] }
        : {
            columns: `repeat(4, ${fr(1)})`,
            rows: `${fr(1.1)} ${fr(1)}`,
            areas: '"s0 s0 s1 s1" "s2 s3 s4 s5"',
            narrow: [false, false, true, true, true, true],
          };
    case 'quad':
      return { columns: two, rows: two, areas: '"s0 s1" "s2 s3"', narrow: [portrait, portrait, portrait, portrait] };
    case 'columns':
      return portrait
        ? { columns: fr(1), rows: `repeat(3, ${fr(1)})`, areas: '"s0" "s1" "s2"', narrow: [false, false, false] }
        : { columns: `repeat(3, ${fr(1)})`, rows: fr(1), areas: '"s0 s1 s2"', narrow: [true, true, true] };
    case 'spotlight':
      return portrait
        ? { columns: two, rows: `${fr(1.3)} ${fr(1)}`, areas: '"s0 s0" "s1 s2"', narrow: [false, true, true] }
        : { columns: `${fr(1.4)} ${fr(1)}`, rows: two, areas: '"s0 s1" "s0 s2"', narrow: [false, false, false] };
    default:
      return homeGrid(count, portrait);
  }
}

export function hiddenWidgets(layout: HomeWidgetId[]): HomeWidgetId[] {
  return HOME_WIDGETS.map((w) => w.id).filter((id) => layout.indexOf(id) < 0);
}

export interface HomeGrid {
  columns: string;
  rows: string;
  areas: string;
  /** Per slot: true when the slot is narrow enough that widgets should use their compact form. */
  narrow: boolean[];
}

const TOP = '272px';
const fr = (n: number) => `minmax(0,${n}fr)`;

/** CSS grid for `count` widgets (slots named s0…s4), landscape or portrait. */
export function homeGrid(count: number, portrait: boolean): HomeGrid {
  const n = Math.max(1, Math.min(MAX_HOME_WIDGETS, count));
  const two = `${fr(1.2)} ${fr(1)}`;
  if (portrait) {
    const cols = `${fr(1)} ${fr(1)}`;
    switch (n) {
      case 1:
        return { columns: fr(1), rows: fr(1), areas: '"s0"', narrow: [false] };
      case 2:
        return { columns: fr(1), rows: `${fr(1)} ${fr(1)}`, areas: '"s0" "s1"', narrow: [false, false] };
      case 3:
        return { columns: cols, rows: `${TOP} ${fr(1)}`, areas: '"s0 s1" "s2 s2"', narrow: [true, true, false] };
      case 4:
        return { columns: cols, rows: `${TOP} ${fr(1)} ${fr(1)}`, areas: '"s0 s1" "s2 s2" "s3 s3"', narrow: [true, true, false, false] };
      default:
        return { columns: cols, rows: `${TOP} ${fr(1)} ${fr(1.1)}`, areas: '"s0 s1" "s2 s2" "s3 s4"', narrow: [true, true, false, true, true] };
    }
  }
  switch (n) {
    case 1:
      return { columns: fr(1), rows: fr(1), areas: '"s0"', narrow: [false] };
    case 2:
      return { columns: two, rows: fr(1), areas: '"s0 s1"', narrow: [false, false] };
    case 3:
      return { columns: two, rows: `${TOP} ${fr(1)}`, areas: '"s0 s1" "s2 s2"', narrow: [false, false, false] };
    case 4:
      return { columns: two, rows: `${TOP} ${fr(1)}`, areas: '"s0 s1" "s2 s3"', narrow: [false, false, false, false] };
    default:
      // Four tracks keep the top row's 1.2 : 1 split while the bottom row holds three widgets.
      return {
        columns: `${fr(0.33)} ${fr(0.215)} ${fr(0.155)} ${fr(0.3)}`,
        rows: `${TOP} ${fr(1)}`,
        areas: '"s0 s0 s1 s1" "s2 s3 s3 s4"',
        narrow: [false, false, true, true, true],
      };
  }
}
